// src/modules/units/tyres/shared/tyre-catalogue-match.js
//
// The one definition of "which tyre catalogue entry is this?". Pure — no DOM, no I/O.
//
// Before this module the answer lived in three places that disagreed: the catalogue
// screen's duplicate check compared seven fields as exact lower-cased text; the New
// Tyre form's auto-fill compared manufacturer/model/tread and a size with its own
// looser normaliser; and the importer needed a third. Two rules that can disagree
// is the dual-source-of-truth this module keeps deleting, so all three now read
// from here.
//
// IDENTITY. A catalogue entry is one composite row (backend schema): manufacturer +
// model + tread pattern + size + application + type + grade. Two specs are the SAME
// entry when all seven agree after normalisation (catalogue duplicate check).
//
// COMPATIBILITY. A tyre is "in the catalogue" when some entry does not CONTRADICT
// it: manufacturer and model must agree, and every other identity field must agree
// wherever BOTH sides have a value. A blank on either side matches anything — an
// entry with no size covers every size, and a tyre with no grade recorded is not
// contradicted by an entry for "New".
//
// NORMALISATION. Text is trimmed and lower-cased. Sizes additionally drop spaces and
// the optional slash before the construction letter, so "315/80R22.5",
// "315/80/R22.5" and "315 / 80 R 22.5" are one size (the rule the form already used).

export const CATALOGUE_IDENTITY_FIELDS = Object.freeze([
  'manufacturerName', 'modelName', 'treadPatternCode', 'tyreSize',
  'tyreApplication', 'tyreType', 'tyreGrade'
]);

// Catalogue fields carried as defaults, not identity.
export const CATALOGUE_DEFAULT_FIELDS = Object.freeze([
  'manufacturerNominalTreadDepth', 'manufacturerExpectedMileage', 'minimumTreadForRetread'
]);

export function normaliseCatalogueValue(value) {
  return String(value ?? '').trim().toLocaleLowerCase();
}

export function normaliseTyreSize(value) {
  return normaliseCatalogueValue(value).replace(/\s+/g, '').replace(/\/r/g, 'r');
}

/** The normaliser a given identity field compares with. */
export function catalogueFieldNormaliser(key) {
  return key === 'tyreSize' ? normaliseTyreSize : normaliseCatalogueValue;
}

/**
 * The catalogue spelling of a size, when `size` is one of `options` written
 * differently ("315/80R22.5" → "315/80/R22.5"); else null.
 */
export function canonicalTyreSize(size, options = []) {
  const k = normaliseTyreSize(size);
  if (!k) return null;
  return options.find((o) => normaliseTyreSize(o) === k) || null;
}

/** Stable key for a spec's identity (for grouping and Set/Map lookups). */
export function catalogueKey(spec = {}) {
  return CATALOGUE_IDENTITY_FIELDS.map((k) => catalogueFieldNormaliser(k)(spec[k])).join('␟');
}

/** Same catalogue entry: all seven identity fields agree. */
export function sameCatalogueIdentity(a = {}, b = {}) {
  return CATALOGUE_IDENTITY_FIELDS.every((k) => {
    const n = catalogueFieldNormaliser(k);
    return n(a[k]) === n(b[k]);
  });
}

/** `a` and `b` don't contradict each other (blank matches anything; manufacturer
 *  and model must be present on both and agree). */
export function catalogueCompatible(a = {}, b = {}) {
  return CATALOGUE_IDENTITY_FIELDS.every((k) => {
    const n = catalogueFieldNormaliser(k);
    const x = n(a[k]);
    const y = n(b[k]);
    if (k === 'manufacturerName' || k === 'modelName') return !!x && x === y;
    return !x || !y || x === y;
  });
}

const filledCount = (spec) => CATALOGUE_IDENTITY_FIELDS.filter((k) => normaliseCatalogueValue(spec[k])).length;

/**
 * The catalogue identity of a tyre record/payload. The tyre record form fills
 * `tyreBrand` from the catalogue's manufacturer typeahead, so that is the
 * manufacturer; `tyreMake` is the fallback for records that only carry a make.
 */
export function catalogueSpecFromTyre(t = {}) {
  const text = (v) => String(v ?? '').trim();
  return {
    manufacturerName: text(t.tyreBrand) || text(t.tyreMake),
    modelName: text(t.tyreModel),
    treadPatternCode: text(t.tyreTreadPattern),
    tyreSize: text(t.tyreSize),
    tyreApplication: text(t.tyreApplication),
    tyreType: text(t.tyreType),
    tyreGrade: text(t.tyreGrade)
  };
}

/** Human label: "Michelin X Multi D · 315/80R22.5 · Drive · Super Single · New". */
export function catalogueSpecLabel(spec = {}) {
  const head = [spec.manufacturerName, spec.modelName].filter(Boolean).join(' ');
  const tread = spec.treadPatternCode && normaliseCatalogueValue(spec.treadPatternCode) !== normaliseCatalogueValue(spec.modelName)
    ? `tread ${spec.treadPatternCode}` : '';
  return [head, tread, spec.tyreSize, spec.tyreApplication, spec.tyreType, spec.tyreGrade].filter(Boolean).join(' · ');
}

/**
 * Look a spec up in the catalogue.
 * @returns {{status:'matched'|'inactive'|'missing'|'incomplete', entry:object|null}}
 *   incomplete — the spec has no manufacturer or no model, so it can't be matched
 *   (and can't become an entry: both are required by the catalogue).
 *   inactive   — the only compatible entries are switched off.
 */
export function matchCatalogue(spec = {}, entries = []) {
  if (!normaliseCatalogueValue(spec.manufacturerName) || !normaliseCatalogueValue(spec.modelName)) {
    return { status: 'incomplete', entry: null };
  }
  let inactive = null;
  for (const e of entries) {
    if (!catalogueCompatible(spec, e)) continue;
    if (e?.isActive === false) { inactive = inactive || e; continue; }
    return { status: 'matched', entry: e };
  }
  return inactive ? { status: 'inactive', entry: inactive } : { status: 'missing', entry: null };
}

/**
 * Plan the catalogue entries needed for a set of tyre payloads.
 *
 * Every payload whose spec is `missing` from `entries` is grouped by identity; a
 * group whose spec is compatible with a MORE specific group is folded into it, so
 * one file never creates both "Michelin X Multi D" and "Michelin X Multi D ·
 * 315/80R22.5 · Drive" when the second covers the first.
 *
 * Defaults are filled only where every tyre in the group agrees:
 *   manufacturerNominalTreadDepth ← initialTyreTreadDepth (the tread when new)
 *   manufacturerExpectedMileage   ← manufacturerExpectedMileage
 *   minimumTreadForRetread        ← minimumTreadForRetread
 *
 * @param {Array<{rowNumber:number, payload:object}>} items
 * @param {object[]} entries the customer's catalogue (active and inactive)
 * @returns {Array<{key:string, spec:object, payload:object, rowNumbers:number[], label:string}>}
 */
export function planCatalogueEntries(items = [], entries = []) {
  const groups = new Map();
  for (const it of items) {
    const spec = catalogueSpecFromTyre(it.payload);
    if (matchCatalogue(spec, entries).status !== 'missing') continue;
    const key = catalogueKey(spec);
    if (!groups.has(key)) groups.set(key, { key, spec, members: [] });
    groups.get(key).members.push(it);
  }

  // Most specific first, then fold less specific groups into a compatible one.
  const ordered = [...groups.values()].sort((a, b) => filledCount(b.spec) - filledCount(a.spec));
  const accepted = [];
  for (const g of ordered) {
    const host = accepted.find((h) => catalogueCompatible(g.spec, h.spec));
    if (host) host.members.push(...g.members);
    else accepted.push(g);
  }

  const agreed = (members, pick) => {
    const vals = members.map((m) => pick(m.payload));
    if (vals.some((v) => v === undefined || v === null || v === '')) return undefined;
    return vals.every((v) => Number(v) === Number(vals[0])) ? Number(vals[0]) : undefined;
  };

  return accepted.map((g) => {
    const payload = { ...g.spec, isActive: true };
    const nominal = agreed(g.members, (p) => p.initialTyreTreadDepth);
    const mileage = agreed(g.members, (p) => p.manufacturerExpectedMileage);
    const minTread = agreed(g.members, (p) => p.minimumTreadForRetread);
    if (nominal !== undefined) payload.manufacturerNominalTreadDepth = nominal;
    if (mileage !== undefined) payload.manufacturerExpectedMileage = mileage;
    if (minTread !== undefined) payload.minimumTreadForRetread = minTread;
    return {
      key: g.key,
      spec: g.spec,
      payload,
      rowNumbers: g.members.map((m) => m.rowNumber).sort((a, b) => a - b),
      label: catalogueSpecLabel(g.spec)
    };
  });
}

// ---------------------------------------------------------------------------
// Catalogue AUTO-FILL — the one rule for "what does the catalogue suggest for
// this tyre's specification?". Used by the tyre record form (live, as fields
// change) and by the import template (precomputed into its lookup table), so the
// two can never suggest different values.
//
//   1. Rows for manufacturer + model (exact, normalised). None → no suggestion.
//   2. Narrow by tread pattern: a given tread keeps its rows; NO tread keeps only
//      the rows without a tread (a blank tread is a specification of its own).
//      When no tread is given and the model has exactly ONE tread pattern, that
//      pattern is suggested (singleCatalogueTread) and the rows narrow to it.
//   3. Narrow by size when a size is known and some rows have it; otherwise keep
//      the step-2 rows (a size the catalogue doesn't know doesn't empty them).
//   4. A field is suggested only when EVERY remaining row has the same non-blank
//      value (commonCatalogueValue) — several sizes of one model with different
//      nominal treads suggest no tread until the size is chosen.
// ---------------------------------------------------------------------------

/** Distinct non-blank values of `key` (first spelling kept), sorted. */
export function uniqueCatalogueValues(rows = [], key) {
  const seen = new Map();
  rows.forEach((row) => {
    const value = String(row?.[key] || '').trim();
    const n = normaliseCatalogueValue(value);
    if (value && !seen.has(n)) seen.set(n, value);
  });
  return Array.from(seen.values()).sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
  );
}

/** The value of `key` shared by every row, or null (blank anywhere / disagreement / no rows). */
export function commonCatalogueValue(rows = [], key, normaliser = normaliseCatalogueValue) {
  if (!rows.length) return null;
  const values = rows.map((row) => row?.[key]);
  if (values.some((value) => value === null || value === undefined || value === '')) return null;
  const first = normaliser(values[0]);
  return values.every((value) => normaliser(value) === first) ? values[0] : null;
}

/** Step 1: rows for one manufacturer + model. */
export function catalogueModelRows(rows = [], manufacturer, model) {
  const m = normaliseCatalogueValue(manufacturer);
  const mo = normaliseCatalogueValue(model);
  if (!m || !mo) return [];
  return rows.filter((row) =>
    normaliseCatalogueValue(row?.manufacturerName) === m &&
    normaliseCatalogueValue(row?.modelName) === mo
  );
}

/** The model's only tread pattern, or null when it has none or several. */
export function singleCatalogueTread(modelRows = []) {
  const treads = uniqueCatalogueValues(modelRows, 'treadPatternCode');
  return treads.length === 1 ? treads[0] : null;
}

/** Steps 2–3: narrow a model's rows by tread pattern, then (softly) by size. */
export function narrowCatalogueRows(modelRows = [], { tread = '', size = '' } = {}) {
  const t = normaliseCatalogueValue(tread);
  const treadRows = t
    ? modelRows.filter((row) => normaliseCatalogueValue(row?.treadPatternCode) === t)
    : modelRows.filter((row) => normaliseCatalogueValue(row?.treadPatternCode) === '');
  if (!treadRows.length) return [];
  const s = normaliseTyreSize(size);
  if (s) {
    const sizeRows = treadRows.filter((row) => normaliseTyreSize(row?.tyreSize) === s);
    if (sizeRows.length) return sizeRows;
  }
  return treadRows;
}

/** Tyre record field ← catalogue field, in the order the form fills them. */
export const CATALOGUE_DEFAULT_MAP = Object.freeze([
  { field: 'tyreSize', from: 'tyreSize', normaliser: normaliseTyreSize },
  { field: 'tyreApplication', from: 'tyreApplication' },
  { field: 'tyreType', from: 'tyreType' },
  { field: 'tyreGrade', from: 'tyreGrade' },
  { field: 'manufacturerExpectedMileage', from: 'manufacturerExpectedMileage', numeric: true },
  { field: 'minimumTreadForRetread', from: 'minimumTreadForRetread', numeric: true },
  { field: 'initialTyreTreadDepth', from: 'manufacturerNominalTreadDepth', numeric: true }
]);

/**
 * Step 4: the suggested value per tyre field for a set of narrowed rows. Every
 * field in CATALOGUE_DEFAULT_MAP is present; null = no suggestion.
 */
export function catalogueDefaultsFor(rows = []) {
  const out = {};
  for (const d of CATALOGUE_DEFAULT_MAP) {
    const v = commonCatalogueValue(rows, d.from, d.normaliser || normaliseCatalogueValue);
    out[d.field] = v === null ? null : (d.numeric ? Number(v) : v);
  }
  return out;
}
